/**
 * Fleet Map — every located device in the operator's scope, plotted on a
 * Leaflet/OSM map with the same filter affordances as the Dashboard and
 * Ops Devices views.
 *
 * History:
 *  - Pre-rewrite this view rendered blank because the bounds calculation
 *    cast an N-point array `[[lat,lon], …]` to `LatLngBoundsLiteral`,
 *    which Leaflet defines as a 2-corner pair `[[s,w],[n,e]]`. The
 *    malformed bounds either threw inside Leaflet or silently aborted
 *    the MapContainer render. The fix lives in <FitToBounds/> below.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import type {
  LatLngBounds,
  Map as LeafletMap,
  Marker as LeafletMarker,
} from 'leaflet';
import {
  ENV_GROUP_LABEL,
  useDeviceGroups,
  useDevices,
  useMe,
  useTenants,
  type DeviceSummary,
  type EnvGroup,
} from '../api/hooks';
import {
  DeviceFilters,
  hasActiveFilters,
  type GroupFilter,
  type OnlineFilter,
} from '../components/DeviceFilters';
import {
  DeviceMapMarker,
  compareForSidebar,
  deviceStatus,
} from '../components/DeviceMapMarker';
import { DeviceClusterMarker } from '../components/DeviceClusterMarker';
import { fmtAgo, useNow } from '../components/format';

const OSM_TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Country-zoom over central Europe — picked so a brand-new fleet (no
// located devices yet) still renders a usable tile background.
const DEFAULT_CENTER: [number, number] = [50, 15];
const DEFAULT_ZOOM = 4;

// Defensive check matching DeviceLocationCard.tsx:20–25 — guards against
// the dead-zone partial-geo shape (`last_scan_request` only, no lat/lon).
function hasValidGeo(d: DeviceSummary): boolean {
  const g = d.geo;
  return (
    g !== null &&
    typeof g.lat === 'number' &&
    typeof g.lon === 'number' &&
    Number.isFinite(g.lat) &&
    Number.isFinite(g.lon)
  );
}

function deviceMatchesSearch(
  d: DeviceSummary,
  q: string,
  group: EnvGroup | null,
): boolean {
  if (!q) return true;
  const haystack = [
    d.thing,
    d.friendly_name,
    d.tenant_id,
    d.serial,
    d.fw_ver,
    group ? ENV_GROUP_LABEL[group] : null,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q.toLowerCase());
}

// ─── MapContainer children (need `useMap`, so they live inside the tree) ───

/**
 * Refits the map to the supplied points whenever `refitKey` changes.
 * Encoding filter state + a points-presence bit in the key prevents the
 * 30 s React Query poll from yanking the viewport around on every cycle
 * while still refitting when the user changes a filter or when the
 * located set first arrives.
 */
function FitToBounds({
  points,
  refitKey,
}: {
  points: [number, number][];
  refitKey: string;
}) {
  const map = useMap();
  useEffect(() => {
    const [first] = points;
    if (!first) return;
    if (points.length === 1) {
      // A zero-area bounds confuses Leaflet's zoom math — use setView.
      map.setView(first, Math.max(map.getZoom(), 13));
      return;
    }
    map.fitBounds(points, { padding: [40, 40] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refitKey]);
  return null;
}

/** Reports current viewport bounds upward on `moveend` / `zoomend`. */
function ViewportTracker({
  onChange,
}: {
  onChange: (b: LatLngBounds) => void;
}) {
  const map = useMap();
  useEffect(() => {
    const update = () => onChange(map.getBounds());
    update(); // seed
    map.on('moveend', update);
    map.on('zoomend', update);
    return () => {
      map.off('moveend', update);
      map.off('zoomend', update);
    };
  }, [map, onChange]);
  return null;
}

/** One-shot capture of the underlying Leaflet map instance. */
function CaptureMap({ onReady }: { onReady: (m: LeafletMap) => void }) {
  const map = useMap();
  useEffect(() => {
    onReady(map);
  }, [map, onReady]);
  return null;
}

/**
 * Single-linkage agglomerative clustering on screen-pixel distance.
 *
 * A device joins a cluster if it's within `thresholdPx` of ANY device
 * already in that cluster (chaining is allowed — three devices A-B-C
 * spaced 20-20 px end-to-end all cluster, even though A↔C is 40 px).
 * For Artome's "devices at the same venue" case this is the desired
 * behaviour: a row of stacked markers should collapse to one.
 *
 * O(n²) — fine at fleet scale (n ≤ ~50). If a tenant ever crosses
 * ~200 located devices, swap for a quadtree / grid index.
 */
export function clusterByPixels(
  map: LeafletMap,
  devices: DeviceSummary[],
  thresholdPx: number,
): DeviceSummary[][] {
  const points = devices.map((d) => ({
    d,
    // Callers filter via hasValidGeo() first, so the non-null assertion
    // on lat/lon is safe at runtime — DE-162 widened the type to nullable.
    p: map.latLngToContainerPoint([d.geo!.lat!, d.geo!.lon!]),
  }));
  const clusters: (typeof points)[] = [];
  for (const item of points) {
    const join = clusters.find((c) =>
      c.some((m) => m.p.distanceTo(item.p) < thresholdPx),
    );
    if (join) join.push(item);
    else clusters.push([item]);
  }
  return clusters.map((c) => c.map((m) => m.d));
}

/**
 * Renders one marker per cluster (or one per device, when a "cluster"
 * has size 1). Lives inside MapContainer so it can use `useMap()` and
 * subscribe to viewport events; re-clusters on every moveend / zoomend
 * because pixel positions shift with zoom.
 */
function MarkerLayer({
  located,
  groupOf,
  registerRef,
  thresholdPx = 28,
}: {
  located: DeviceSummary[];
  groupOf: Map<string, EnvGroup>;
  registerRef: (thing: string, marker: LeafletMarker | null) => void;
  thresholdPx?: number;
}) {
  const map = useMap();
  // `tick` bumps on every viewport change. We don't use the value —
  // it's just a dependency that forces `clusters` to recompute when
  // `latLngToContainerPoint` would yield different pixel positions.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    map.on('moveend', bump);
    map.on('zoomend', bump);
    return () => {
      map.off('moveend', bump);
      map.off('zoomend', bump);
    };
  }, [map]);

  const clusters = useMemo(
    () => clusterByPixels(map, located, thresholdPx),
    // `tick` is intentionally a dep even though we don't read it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [located, tick, map, thresholdPx],
  );

  return (
    <>
      {clusters.map((cluster) => {
        if (cluster.length === 1) {
          const d = cluster[0]!;
          return (
            <DeviceMapMarker
              key={d.thing}
              device={d}
              group={groupOf.get(d.thing) ?? null}
              registerRef={registerRef}
            />
          );
        }
        // Composite key from sorted member things — stable across
        // viewport changes when membership is unchanged, so React
        // keeps the marker DOM and the popup state survives a pan.
        const key = `cluster:${cluster
          .map((d) => d.thing)
          .sort()
          .join(',')}`;
        return (
          <DeviceClusterMarker
            key={key}
            devices={cluster}
            groupOf={groupOf}
            registerRef={registerRef}
          />
        );
      })}
    </>
  );
}

// ─── Main component ─────────────────────────────────────────────────────

export function FleetMap() {
  const me = useMe();
  const devices = useDevices();
  const groupsQ = useDeviceGroups();
  const tenants = useTenants();
  // Keep "Last seen" relative times in the sidebar fresh — same cadence
  // as Dashboard.
  useNow(30_000);

  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState<GroupFilter>('all');
  const [tenantFilter, setTenantFilter] = useState<string>('all');
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>('all');
  const [viewportBounds, setViewportBounds] = useState<LatLngBounds | null>(
    null,
  );

  const mapRef = useRef<LeafletMap | null>(null);
  const markerRefs = useRef<Record<string, LeafletMarker | null>>({});

  // thing → env-group (Dashboard:45–48 pattern). Tenant-admins get 403
  // on /admin/device-groups; the map is empty for them and group filter
  // simply has no effect.
  const groupOf = new Map<string, EnvGroup>();
  for (const card of groupsQ.data?.groups ?? []) {
    for (const thing of card.devices) groupOf.set(thing, card.name);
  }

  const filteredRows = useMemo(() => {
    const all = devices.data?.devices ?? [];
    return all.filter((d) => {
      const g = groupOf.get(d.thing) ?? null;
      if (groupFilter !== 'all' && g !== groupFilter) return false;
      if (tenantFilter !== 'all' && d.tenant_id !== tenantFilter) return false;
      if (onlineFilter === 'online' && !d.online) return false;
      if (onlineFilter === 'offline' && d.online) return false;
      if (!deviceMatchesSearch(d, search, g)) return false;
      return true;
    });
    // groupOf is rebuilt every render from groupsQ.data — depend on the
    // source to avoid stale-closure issues. Same convention as Dashboard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    devices.data,
    groupsQ.data,
    groupFilter,
    tenantFilter,
    onlineFilter,
    search,
  ]);

  const located = filteredRows.filter(hasValidGeo);
  const unlocated = filteredRows.filter((d) => !hasValidGeo(d));

  const points: [number, number][] = located.map((d) => [
    d.geo!.lat!,
    d.geo!.lon!,
  ]);
  // Refit on filter change or when the located set first appears /
  // changes size. Stable across plain data refreshes.
  const refitKey = `${search}|${groupFilter}|${tenantFilter}|${onlineFilter}|${points.length}`;

  // Split located into in-viewport vs off-map for the sidebar.
  const { inView, offMap } = useMemo(() => {
    if (!viewportBounds) {
      return { inView: located, offMap: [] as DeviceSummary[] };
    }
    const south = viewportBounds.getSouth();
    const north = viewportBounds.getNorth();
    const west = viewportBounds.getWest();
    const east = viewportBounds.getEast();
    const inV: DeviceSummary[] = [];
    const off: DeviceSummary[] = [];
    for (const d of located) {
      // `located` is pre-filtered by hasValidGeo(), so the assertions
      // are safe (DE-162 widened the type to nullable).
      const lat = d.geo!.lat!;
      const lon = d.geo!.lon!;
      const inside =
        lat >= south && lat <= north && lon >= west && lon <= east;
      (inside ? inV : off).push(d);
    }
    return { inView: inV, offMap: off };
  }, [located, viewportBounds]);

  const totalCount = devices.data?.devices.length ?? 0;
  const showTenantFilter = me.data?.scope === 'all';
  const hasFilters = hasActiveFilters(
    search,
    groupFilter,
    tenantFilter,
    onlineFilter,
  );
  const noGeoAtAll =
    totalCount > 0 &&
    (devices.data?.devices ?? []).every((d) => !hasValidGeo(d));

  const clearAll = () => {
    setSearch('');
    setGroupFilter('all');
    setTenantFilter('all');
    setOnlineFilter('all');
  };

  const handleFitView = () => {
    const m = mapRef.current;
    const [first] = points;
    if (!m || !first) return;
    if (points.length === 1) {
      m.setView(first, Math.max(m.getZoom(), 13));
      return;
    }
    m.fitBounds(points, { padding: [40, 40] });
  };

  const handleFlyTo = useCallback(
    (thing: string, lat: number, lon: number) => {
      const m = mapRef.current;
      if (!m) return;
      m.flyTo([lat, lon], Math.max(m.getZoom(), 13), { duration: 0.5 });
      const marker = markerRefs.current[thing];
      if (marker) marker.openPopup();
    },
    [],
  );

  const onViewportChange = useCallback((b: LatLngBounds) => {
    setViewportBounds(b);
  }, []);

  if (devices.isLoading) {
    return <p className="text-sm text-muted">Loading…</p>;
  }
  if (devices.error) {
    return (
      <p className="text-sm text-warn">
        {(devices.error as Error).message}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Map</h1>
          <p className="text-sm text-muted tabular-nums">
            {located.length} located · {unlocated.length} without location ·{' '}
            {totalCount} total
          </p>
        </div>
      </div>

      <DeviceFilters
        search={search}
        setSearch={setSearch}
        groupFilter={groupFilter}
        setGroupFilter={setGroupFilter}
        tenantFilter={tenantFilter}
        setTenantFilter={setTenantFilter}
        onlineFilter={onlineFilter}
        setOnlineFilter={setOnlineFilter}
        tenants={tenants.data?.tenants ?? []}
        showTenant={showTenantFilter}
        searchPlaceholder="Search thing, name, tenant, group, serial, FW…"
        refresh={() => void devices.refetch()}
        isFetching={devices.isFetching}
        endSlot={
          // Trailing "view controls" cluster — match count, divider,
          // Fit view, Clear filters. Reads as a single toolbar block
          // rather than three orphan affordances.
          <div className="flex items-center gap-3 ml-auto">
            {hasFilters && (
              <>
                <span className="text-xs text-muted tabular-nums">
                  {filteredRows.length} of {totalCount} match
                </span>
                <span
                  className="w-px h-5 bg-border"
                  aria-hidden="true"
                />
              </>
            )}
            <button
              type="button"
              onClick={handleFitView}
              disabled={located.length === 0}
              title="Frame all located devices"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border border-border rounded-md bg-white text-fg transition-colors hover:bg-soft focus:outline-none focus-visible:ring-1 focus-visible:ring-accent disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
            >
              {/* Four corner-bracket "fit-to-bounds" glyph — reads as
                  a frame snapping shut around content. */}
              <svg
                viewBox="0 0 12 12"
                width="12"
                height="12"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                strokeLinecap="round"
                aria-hidden="true"
              >
                <path d="M2 4V2h2 M10 4V2H8 M2 8v2h2 M10 8v2H8" />
              </svg>
              Fit view
            </button>
            {hasFilters && (
              <button
                type="button"
                onClick={clearAll}
                className="text-xs text-accent hover:underline focus:outline-none focus-visible:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-4">
        <div className="rounded-md overflow-hidden border border-border">
          <MapContainer
            center={DEFAULT_CENTER}
            zoom={DEFAULT_ZOOM}
            scrollWheelZoom={true}
            style={{
              height: 'calc(100vh - 240px)',
              minHeight: 480,
              width: '100%',
            }}
          >
            <CaptureMap
              onReady={(m) => {
                mapRef.current = m;
              }}
            />
            <TileLayer url={OSM_TILES} attribution={OSM_ATTR} />
            <FitToBounds points={points} refitKey={refitKey} />
            <ViewportTracker onChange={onViewportChange} />
            <MarkerLayer
              located={located}
              groupOf={groupOf}
              registerRef={(thing, m) => {
                markerRefs.current[thing] = m;
              }}
            />
          </MapContainer>
        </div>

        <aside className="space-y-3">
          {noGeoAtAll && (
            <div className="card text-xs text-muted">
              No devices have reported a location yet. The geolocation Lambda
              publishes once a device uploads its first WiFi/cell scan.
            </div>
          )}

          {!noGeoAtAll && filteredRows.length === 0 && (
            <div className="card text-xs space-y-2">
              <p className="text-muted">No devices match these filters.</p>
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-accent hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          )}

          {filteredRows.length === 0 && totalCount === 0 && (
            <div className="card text-xs text-muted">
              No devices in your tenant yet.
            </div>
          )}

          {filteredRows.length > 0 && (
            <>
              <SidebarSection
                title="In view"
                count={inView.length}
                rows={inView}
                groupOf={groupOf}
                emptyHint="Pan or zoom out to bring devices into view."
                onRowClick={(d) =>
                  handleFlyTo(d.thing, d.geo!.lat!, d.geo!.lon!)
                }
              />
              {offMap.length > 0 && (
                <SidebarSection
                  title="Off-map"
                  count={offMap.length}
                  rows={offMap}
                  groupOf={groupOf}
                  onRowClick={(d) =>
                    handleFlyTo(d.thing, d.geo!.lat!, d.geo!.lon!)
                  }
                />
              )}
              {unlocated.length > 0 && (
                <SidebarSection
                  title="No location"
                  count={unlocated.length}
                  rows={unlocated}
                  groupOf={groupOf}
                  asLinks
                />
              )}
            </>
          )}
        </aside>
      </div>
    </div>
  );
}

// ─── Sidebar section ────────────────────────────────────────────────────
// `compareForSidebar` lives in `DeviceMapMarker.tsx` so the cluster
// popup can share the same sort without a circular import.

function SidebarSection({
  title,
  count,
  rows,
  groupOf,
  emptyHint,
  onRowClick,
  asLinks,
}: {
  title: string;
  count: number;
  rows: DeviceSummary[];
  groupOf: Map<string, EnvGroup>;
  emptyHint?: string;
  onRowClick?: (d: DeviceSummary) => void;
  asLinks?: boolean;
}) {
  // Sort a copy so the parent's array (and React Query's cache) stays
  // untouched. O(n log n) on ≤ fleet-size n is negligible.
  const sortedRows = [...rows].sort(compareForSidebar);
  return (
    <section className="card">
      <header className="flex items-baseline justify-between mb-2">
        <h2 className="font-medium text-sm">{title}</h2>
        <span className="text-xs text-muted tabular-nums">{count}</span>
      </header>
      {sortedRows.length === 0 ? (
        <p className="text-xs text-muted">{emptyHint ?? 'None.'}</p>
      ) : (
        <ul className="space-y-0.5 max-h-72 overflow-y-auto -mx-1.5">
          {sortedRows.map((d) => {
            const group = groupOf.get(d.thing) ?? null;
            const status = deviceStatus(d);
            const dotClass =
              status === 'online'
                ? 'bg-accent'
                : status === 'stale'
                  ? 'bg-warn'
                  : 'bg-muted';
            // Identity label: the friendly name reads as the heading
            // when set, with the bare thing ID as a hover-revealed
            // tooltip. When no friendly name exists, the thing ID is
            // the heading (rendered mono for parseability).
            const inner = (
              <>
                <span
                  className={`inline-block w-2 h-2 rounded-full shrink-0 ring-1 ring-white/80 ${dotClass}`}
                  aria-label={status}
                />
                <span
                  className={
                    d.friendly_name
                      ? 'text-xs flex-1 min-w-0 truncate font-medium text-fg'
                      : 'text-xs flex-1 min-w-0 truncate font-mono text-fg'
                  }
                  title={d.thing}
                >
                  {d.friendly_name ?? d.thing}
                </span>
                {group && (
                  <span className="pill-muted shrink-0">
                    {ENV_GROUP_LABEL[group]}
                  </span>
                )}
                <span className="text-[10px] text-muted tabular-nums tracking-wide shrink-0">
                  {fmtAgo(d.last_seen)}
                </span>
                <span
                  className="text-muted text-xs leading-none shrink-0 opacity-0 -mr-0.5 transition-opacity group-hover/row:opacity-70 group-focus-visible/row:opacity-100"
                  aria-hidden="true"
                >
                  ›
                </span>
              </>
            );
            // `group/row` lets the chevron react to hover/focus on the
            // whole row, not just itself. focus-visible ring uses the
            // accent-soft token so keyboard nav stands out without
            // shouting.
            const rowClass =
              'group/row flex items-center gap-2 px-1.5 py-1 rounded-md text-left w-full transition-colors hover:bg-soft focus:outline-none focus-visible:bg-soft focus-visible:ring-1 focus-visible:ring-accent';
            return (
              <li key={d.thing}>
                {asLinks ? (
                  <Link
                    to={`/devices/${encodeURIComponent(d.thing)}`}
                    className={rowClass}
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={rowClass}
                    onClick={() => onRowClick?.(d)}
                  >
                    {inner}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
